import type { JSX } from 'preact';
import { formatElapsed, HIGH_SCORE_LIMIT, type HighScore } from '../../sim/highScores/index.ts';
import { setPanel } from '../panels.ts';
import './finishPopup.css';

export type FinishPopupActions = {
  onRaceAgain: () => void;
  /** Plays the run just finished. Omitted when that run has no stored replay. */
  onShowReplay?: () => void;
  /** Returns to the map list, or to the editor when the race was launched as a test-play. */
  onLeave: () => void;
  leaveLabel: string;
};

export type FinishScoreRow = {
  score: HighScore;
  current?: boolean;
  /** Hull tint for a seat from the race just finished. */
  color?: string;
};

export type FinishPopupProps = {
  actions: FinishPopupActions;
  scores?: readonly FinishScoreRow[];
  onSelectScore?: (score: HighScore) => void;
};

export type FinishPopup = {
  destroy: () => void;
};

function scoreBoatName(score: HighScore): string {
  const name = score.boat.name.trim();
  return name || score.boat.id;
}

function scoreLabel(score: HighScore): string {
  const turns = score.rounds === 1 ? '1 turn' : `${score.rounds} turns`;
  return `${scoreBoatName(score)} · ${formatElapsed(score.calculatedTimeMs)} · ${turns}`;
}

export function FinishPopupPanel({
  actions,
  scores = [],
  onSelectScore,
}: FinishPopupProps): JSX.Element {
  return (
    <div class="finish-popup">
      <div class="finish-popup-actions" aria-label="Race finished">
        <button type="button" class="ui-button" onClick={actions.onRaceAgain}>
          Race again
        </button>
        {actions.onShowReplay ? (
          <button type="button" class="ui-button" onClick={actions.onShowReplay}>
            Show replay
          </button>
        ) : (
          <></>
        )}
        <button type="button" class="ui-button" onClick={actions.onLeave}>
          {actions.leaveLabel}
        </button>
      </div>
      {scores.length > 0 ? (
        <section class="finish-popup-scores" aria-label="Top times">
          <p class="finish-popup-scores-title">Top {HIGH_SCORE_LIMIT} times</p>
          <ol>
            {scores.map((row, index) => {
              const { score, current, color } = row;
              const label = scoreLabel(score);
              const playable = score.replay !== null && onSelectScore !== undefined;

              return (
                <li
                  key={`${score.recordedAt}-${score.calculatedTimeMs}-${index}`}
                  class="finish-popup-score"
                  data-current={current ? 'true' : undefined}
                >
                  {color ? (
                    <span
                      class="finish-popup-score-dot"
                      style={{ background: color }}
                      aria-hidden="true"
                    />
                  ) : null}
                  {playable ? (
                    <button
                      type="button"
                      class="finish-popup-score-button"
                      aria-label={`Replay ${scoreBoatName(score)} ${formatElapsed(score.calculatedTimeMs)}`}
                      onClick={() => onSelectScore(score)}
                    >
                      {label}
                    </button>
                  ) : (
                    <span class="finish-popup-score-label">{label}</span>
                  )}
                </li>
              );
            })}
          </ol>
        </section>
      ) : null}
    </div>
  );
}

export function mountFinishPopup(props: FinishPopupProps): FinishPopup {
  setPanel('finishPopup', props);

  return {
    destroy: () => setPanel('finishPopup', null),
  };
}
